/**
 * MKS-393 — the six merch selects, shared by the /profile card and the admin
 * user detail card so the two cannot offer different options.
 *
 * Every option list AND every caption comes from `constants/merchSizes`; no
 * component spells a size or a field name out. The captions are shared with
 * the HR table and the exports (`MERCH_FIELD_LABELS`), so "Shoe size (EU)"
 * cannot mean one thing on /profile and another in the spreadsheet a vendor
 * is sent. Each field is optional and clearable through its own blank option,
 * which is why there is no separate Clear button per row.
 *
 * Presentation-only: it holds no state and performs no write. The parent owns
 * the draft and the save, because the two parents save to different endpoints
 * (self-service PATCH vs. the HR PUT).
 */
import {
  MERCH_CUTS,
  MERCH_FIELD_LABELS,
  MERCH_GARMENT_SIZES,
  MERCH_SHOE_SIZES,
  MERCH_SOCK_SIZES,
  formatShoeSize,
  type MerchSizes,
} from '../../constants/merchSizes';
import type { MerchChartSummary } from '../../constants/merchSizeCharts';
import { GarmentSizeChartLinks, SizeChartLink } from './SizeChartLink';

const CUT_LABELS: Record<(typeof MERCH_CUTS)[number], string> = {
  // What a vendor prints as "unisex" IS the men block, so saying so here
  // stops people picking `women` because `men` looked wrong for them.
  men: 'Men / unisex',
  women: 'Women',
};

const SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500';

interface MerchSizeFieldsProps {
  value: MerchSizes;
  onChange: (patch: Partial<MerchSizes>) => void;
  disabled?: boolean;
  /**
   * MKS-393 size charts — the charts that exist, straight from
   * `useMerchSizeCharts`. Optional and undefined-safe: a parent that does not
   * pass it (or passes it before the query resolves) simply renders no "Size
   * chart" links, which is the same thing the component does when HR has
   * uploaded nothing.
   *
   * The links hang off the four SIZE fields only. A cut is men vs. women, not
   * a measurement, so a chart would say nothing about it — but it does pick
   * WHICH chart the T-shirt and hoodie links open, which is what
   * `GarmentSizeChartLinks` reads `value.merch*Cut` for.
   */
  charts?: MerchChartSummary[];
}

export function MerchSizeFields({
  value,
  onChange,
  disabled = false,
  charts,
}: MerchSizeFieldsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field
        label={MERCH_FIELD_LABELS.merchTshirtSize}
        action={
          <GarmentSizeChartLinks garment="tshirt" cut={value.merchTshirtCut} charts={charts} />
        }
      >
        <select
          aria-label={MERCH_FIELD_LABELS.merchTshirtSize}
          className={SELECT_CLASS}
          disabled={disabled}
          value={value.merchTshirtSize ?? ''}
          onChange={(event) =>
            onChange({
              merchTshirtSize: (event.target.value || null) as MerchSizes['merchTshirtSize'],
            })
          }
        >
          <option value="">Not set</option>
          {MERCH_GARMENT_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </Field>

      <Field label={MERCH_FIELD_LABELS.merchTshirtCut}>
        <select
          aria-label={MERCH_FIELD_LABELS.merchTshirtCut}
          className={SELECT_CLASS}
          disabled={disabled}
          value={value.merchTshirtCut ?? ''}
          onChange={(event) =>
            onChange({
              merchTshirtCut: (event.target.value || null) as MerchSizes['merchTshirtCut'],
            })
          }
        >
          <option value="">Not set</option>
          {MERCH_CUTS.map((cut) => (
            <option key={cut} value={cut}>
              {CUT_LABELS[cut]}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label={MERCH_FIELD_LABELS.merchHoodieSize}
        action={
          <GarmentSizeChartLinks garment="hoodie" cut={value.merchHoodieCut} charts={charts} />
        }
      >
        <select
          aria-label={MERCH_FIELD_LABELS.merchHoodieSize}
          className={SELECT_CLASS}
          disabled={disabled}
          value={value.merchHoodieSize ?? ''}
          onChange={(event) =>
            onChange({
              merchHoodieSize: (event.target.value || null) as MerchSizes['merchHoodieSize'],
            })
          }
        >
          <option value="">Not set</option>
          {MERCH_GARMENT_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </Field>

      <Field label={MERCH_FIELD_LABELS.merchHoodieCut}>
        <select
          aria-label={MERCH_FIELD_LABELS.merchHoodieCut}
          className={SELECT_CLASS}
          disabled={disabled}
          value={value.merchHoodieCut ?? ''}
          onChange={(event) =>
            onChange({
              merchHoodieCut: (event.target.value || null) as MerchSizes['merchHoodieCut'],
            })
          }
        >
          <option value="">Not set</option>
          {MERCH_CUTS.map((cut) => (
            <option key={cut} value={cut}>
              {CUT_LABELS[cut]}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label={MERCH_FIELD_LABELS.merchSockSize}
        action={<SizeChartLink item="socks" charts={charts} />}
      >
        <select
          aria-label={MERCH_FIELD_LABELS.merchSockSize}
          className={SELECT_CLASS}
          disabled={disabled}
          value={value.merchSockSize ?? ''}
          onChange={(event) =>
            onChange({
              merchSockSize: (event.target.value || null) as MerchSizes['merchSockSize'],
            })
          }
        >
          <option value="">Not set</option>
          {MERCH_SOCK_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label={MERCH_FIELD_LABELS.merchShoeSize}
        action={<SizeChartLink item="shoes" charts={charts} />}
      >
        <select
          aria-label={MERCH_FIELD_LABELS.merchShoeSize}
          className={SELECT_CLASS}
          disabled={disabled}
          value={value.merchShoeSize === null ? '' : formatShoeSize(value.merchShoeSize)}
          onChange={(event) =>
            onChange({
              merchShoeSize: event.target.value === '' ? null : Number(event.target.value),
            })
          }
        >
          <option value="">Not set</option>
          {MERCH_SHOE_SIZES.map((size) => (
            <option key={size} value={formatShoeSize(size)}>
              {formatShoeSize(size)}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

/**
 * `action` is the optional "Size chart" link for this field (MKS-393 size
 * charts). It sits beside the caption rather than inside it, and the wrapper
 * is a `<div>` rather than the `<label>` it used to be: a `<button>` nested
 * in a `<label>` activates the labelled control on click, so opening a chart
 * would also yank focus into the select behind the lightbox. Every select
 * carries its own `aria-label`, so the accessible name is unchanged by the
 * swap.
 */
function Field({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="block">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="block text-xs text-gray-500">{label}</span>
        {action}
      </div>
      {children}
    </div>
  );
}
